import React, { useState, useEffect } from "react";
import { Route, Routes, Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { db } from "./firebaseConfig";
import { collection, addDoc } from "firebase/firestore";
import Contact from "./Contact"; // Ensure this file exists
import Donate from "./Donate"; // Ensure this file exists
import Give from "./Matching"; // Ensure this file exists


const App: React.FC = () => {
  const { register, handleSubmit, formState: { errors } } = useForm<{ email: string }>();
  const [submitted, setSubmitted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollToStory) {
      document.getElementById("story")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  const onSubmit = async (data: { email: string }) => {
    try {
      await addDoc(collection(db, "emails"), { email: data.email, timestamp: new Date() });
      setSubmitted(true);
      console.log("Email successfully saved:", data.email);
    } catch (error) {
      console.error("Error adding document: ", error);
    }
  };

  return (
      <div className="bg-[#FAF0E6] text-gray-800 font-alice">
        <header className="flex items-center justify-between px-4 py-3 bg-white shadow-md sticky top-0 z-50">
          {/* Hamburger Menu - Only on Small Screens */}
          <button 
            className="lg:hidden text-gray-700 text-xl font-light focus:outline-none" 
            onClick={toggleMenu}
          >
            {menuOpen ? "╳" : "☰"}
          </button>

          {/* Logo - Centered in Mobile */}
          <Link to="/" className="flex items-center space-x-2 mx-auto lg:mx-0 lg:ml-4">
            <img src="/logo512.png" alt="Wasaela Logo" className="w-10 h-10" />
            <span className="text-xl font-alice text-gray-800">Wasaela Foundation</span>
          </Link>

          {/* Navigation Links - Always Visible on Desktop */}
          <nav className={`absolute top-full left-0 w-full bg-white shadow-md transform transition-all duration-300 ease-in-out lg:static lg:w-auto lg:shadow-none lg:flex lg:items-center lg:space-x-8 ${menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'} lg:opacity-100 lg:max-h-full`}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-8 px-4 py-4 lg:p-0">
              <Link to="/" state={{ scrollToStory: true }} className="text-gray-700 font-medium py-2 hover:text-gray-900">About</Link>
              <Link to="/contact" className="text-gray-700 font-medium py-2 hover:text-gray-900">Contact</Link>
              <Link to="/Matching" className="text-gray-700 font-medium py-2 hover:text-gray-900">Matching Gifts</Link>
              <Link to="/donate" className="bg-[#9CAF88] hover:bg-[#7f996e] text-white px-4 py-2 rounded-lg mt-2 lg:mt-0 transition">Donate</Link>
            </div>
          </nav>
        </header>

        {/* Define Routes */}
        <Routes>
          <Route path="/" element={
            <>
              {/* Hero Section */}
              <section className="relative min-h-screen flex items-center justify-start px-6 text-left" style={{
                backgroundImage: "url('/hero.jpg')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}>
                <div className="absolute inset-0 bg-black bg-opacity-40" />
                <div className="relative z-10 max-w-2xl">
                  <h1 className="text-5xl md:text-6xl font-bold text-white font-alice mb-6">
                    Empowering Communities, Transforming Lives
                  </h1>
                  <p className="text-lg text-white mb-6">
                    Our mission is to create a brighter future through access to clean water, safe housing, quality healthcare, and education for underserved communities.
                  </p>
                  <a href="/donate" className="bg-[#9CAF88] hover:bg-[#7f996e] text-white px-6 py-3 rounded-lg text-lg transition">
                    Donate
                  </a>
                </div>
              </section>
              {/* Our Story and Vision */}
              <section id="story" className="py-16 px-8 bg-[#FAF0E6] shadow-lg border-t border-gray-200">
                <h2 className="text-3xl font-bold text-center font-alice">Our Story and Vision</h2>
                <p className="mt-10 text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
                  The word <em>Wasaela</em> (وسائل) was carefully chosen for its profound meaning. In Arabic, it signifies "means" or "pathways," 
                  perfectly encapsulating our role as a bridge that connects underserved communities to essential resources such as clean water, 
                  housing, healthcare, and education.
                </p>
                <p className="mt-6 text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
                  At <strong>Wasaela Foundation</strong>, we are dedicated to addressing the critical needs of underserved communities. 
                  By providing access to these essentials, we empower individuals and families to build brighter, self-reliant futures.
                </p>
                <p className="mt-6 text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
                  Our vision includes integrating <strong>data-driven AI insights</strong> to identify high-need areas, optimize resource allocation, 
                  and create <strong>sustainable, impactful solutions</strong>. Together, through <strong>compassion, collaboration, and innovation</strong>, 
                  we strive to transform lives and build a brighter future for generations to come.
                </p>
              </section>
              {/* Stay Connected */}
              <section className="py-16 px-8 text-center bg-[#DAF0F7] shadow-lg border-t border-gray-200">
                <h3 className="text-3xl font-bold font-alice">
                  Stay Connected
                </h3>
                <p className="mt-6 text-lg text-gray-600">
                  Be the first to know about our AI-driven initiatives and upcoming projects. Sign up to receive updates and join us in creating sustainable solutions.
                </p>
                {!submitted ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="mt-8 flex flex-col items-center space-y-4"
                  >
                    <input
                      {...register("email", { required: "Email is required" })}
                      type="email"
                      placeholder="Enter your email"
                      className="p-3 border border-gray-300 rounded-md w-full max-w-md"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm">{errors.email.message}</p>
                    )}
                    <button
                      type="submit"
                      className="bg-[#9CAF88] text-white px-6 py-3 rounded-md hover:bg-[#7f996e] transition-transform hover:scale-105"
                    >
                      Notify Me
                    </button>
                  </form>
                ) : (
                  <p className="text-lg text-green-600 mt-6">
                    Thank you for subscribing!
                  </p>
                )}
              </section>
            </>
          } />
          <Route path="/contact" element={<Contact />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/Matching" element={<Give />} />
          
        </Routes>

        {/* Footer */}
        <footer className="bg-black text-white py-10 px-8">
  <div className="text-center">
    <h2 className="text-lg font-alice">Wasaela Foundation</h2>
    <p className="text-gray-400 mt-2">
      Dedicated to empowering communities worldwide through clean water, housing, healthcare, and education.
    </p>
    <p className="mt-4 text-gray-500">
      &copy; 2025 Wasaela Foundation. All rights reserved.
    </p>
    <p className="text-gray-400 mt-2">
      A 501(c)(3) Tax-Exempt Organization | EIN: <strong>33-3590845</strong>
    </p>
    <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-400">
      <a
        href="https://www.facebook.com/profile.php?id=61574191606536"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 hover:text-white transition"
        aria-label="Follow Wasaela Foundation on Facebook"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.23.2 2.23.2v2.46h-1.25c-1.23 0-1.62.77-1.62 1.56V12h2.76l-.44 2.89h-2.32v6.99A10 10 0 0 0 22 12z" />
        </svg>
        <span className="font-medium">Facebook</span>
        <span className="text-gray-500">·</span>
        <span className="underline">Follow us</span>
      </a>
    </div>
  </div>
</footer>
      </div>
  );
};

export default App;